import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "@/shared/hooks/useInView";
import type { Ad, ImageContent, VideoContent } from "@models/Ad.model";
import type { AdCardRendererProps } from "../types";

type MediaContent = ImageContent | VideoContent;

function getContent(ad: Ad): MediaContent | undefined {
    return ad.content as MediaContent | undefined;
}

function safeHttpUrl(raw: string | undefined): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
        const u = new URL(withScheme);
        if (u.protocol !== "http:" && u.protocol !== "https:") return null;
        return u.toString();
    } catch {
        return null;
    }
}

function prettyHost(href: string): string {
    try {
        const u = new URL(href);
        return `${u.host}${u.pathname === "/" ? "" : u.pathname}`;
    } catch {
        return href;
    }
}

function Thumb({ ad, resolved, onPickCover, pickedCover }: Pick<AdCardRendererProps, "ad" | "resolved" | "onPickCover" | "pickedCover">) {
    const media = resolved?.media;
    const preview = resolved?.preview;
    const isVideo = ad.type === "video";
    // The cover is what a network shows before the video plays, and it was invisible
    // here: the poster falls back to a LayeredMedia preview, so a card could look fine
    // while the ad had no cover at all and could not publish (MPN-1761).
    const cover = isVideo ? (getContent(ad) as VideoContent | undefined)?.thumbnail : null;
    // Only offer the control when the picker could actually open: it needs the video.
    const canPickCover = isVideo && !!onPickCover && !!media?.storage_uri;

    const [inViewRef, inView] = useInView("200px");
    const videoRef = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);
    const [videoLoading, setVideoLoading] = useState(false);

    const togglePlay = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const vid = videoRef.current;
        if (!vid) return;
        if (playing) { vid.pause(); setPlaying(false); return; }
        if (vid.readyState < 3) {
            setVideoLoading(true);
            const onCanPlay = () => {
                vid.removeEventListener("canplay", onCanPlay);
                setVideoLoading(false);
                void vid.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
            };
            vid.addEventListener("canplay", onCanPlay);
            vid.load();
            return;
        }
        void vid.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }, [playing]);

    return (
        <div
            ref={inViewRef}
            className="adcard-media__thumb"
        >
            {(!inView || (!preview?.storage_uri && !media?.storage_uri)) && (
                <div className="adcard-media__skeleton" />
            )}
            {inView && !isVideo && (preview?.storage_uri || media?.storage_uri) && (
                <img src={(preview?.storage_uri || media?.storage_uri) ?? undefined} alt="" draggable={false} />
            )}
            {canPickCover && (
                <button
                    type="button"
                    className={`adcard-media__cover${cover || pickedCover ? "" : " adcard-media__cover--none"}`}
                    title={cover || pickedCover
                        ? "This frame is the cover sent to the network. Click to change it."
                        : "No cover yet. Click to choose the frame the network shows."}
                    onClick={(e) => { e.stopPropagation(); onPickCover?.(); }}
                >
                    <i className="bi bi-crop" />
                    {cover || pickedCover ? "Cover" : "Set cover"}
                </button>
            )}
            {inView && isVideo && (
                <>
                    <video
                        ref={videoRef}
                        src={media?.storage_uri ?? undefined}
                        // Matches the cover picker, deliberately. Without it this request caches a
                        // response with no CORS headers recorded, and the picker's crossOrigin request
                        // for the SAME url reuses that entry and fails with "No
                        // Access-Control-Allow-Origin header" -- though S3 sends one (MPN-1761).
                        crossOrigin="anonymous"
                        poster={pickedCover ?? preview?.storage_uri ?? undefined}
                        playsInline
                        preload="metadata"
                        onEnded={() => setPlaying(false)}
                        draggable={false}
                    />
                    <div
                        role="button"
                        tabIndex={0}
                        className={`adcard-media__play${playing ? " adcard-media__play--playing" : ""}`}
                        onClick={togglePlay}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                togglePlay(e as unknown as React.MouseEvent);
                            }
                        }}
                    >
                        {videoLoading ? (
                            <span className="adcard-media__spinner" />
                        ) : playing ? (
                            <svg width="16" height="16" viewBox="0 0 16 16">
                                <rect x="4" y="3" width="3" height="10" fill="#333" />
                                <rect x="9" y="3" width="3" height="10" fill="#333" />
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16">
                                <path d="M5.5 3L12.5 8L5.5 13V3Z" fill="#333" />
                            </svg>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default function AdCardMedia({ ad, mode, resolved, onOpenFull, onPickCover, pickedCover }: AdCardRendererProps) {
    const content = getContent(ad);
    const copy = content?.copy_text;
    const headline = content?.link_headline;
    const description = content?.link_description;
    const cta = content?.cta_text;
    const href = safeHttpUrl(content?.link);

    const copyInnerRef = useRef<HTMLDivElement>(null);
    const [isClamped, setIsClamped] = useState(false);
    useEffect(() => {
        if (mode !== "detail") { setIsClamped(false); return; }
        const el = copyInnerRef.current;
        if (!el) return;
        const check = () => setIsClamped(el.scrollHeight > el.clientHeight + 1);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [mode, copy]);

    return (
        <>
            <Thumb ad={ad} resolved={resolved} onPickCover={onPickCover} pickedCover={pickedCover} />
            {mode !== "compact" && (
                <>
                    <div className={`adcard-media__copy${mode === "full" ? " adcard-media__copy--full" : ""}`}>
                        <div ref={copyInnerRef} className="adcard-media__copy-inner">
                            {copy || <span className="adcard-media__copy-empty">No caption</span>}
                        </div>
                        {isClamped && onOpenFull && (
                            <span
                                role="button"
                                tabIndex={0}
                                className="adcard-media__more"
                                onClick={(e) => { e.stopPropagation(); onOpenFull(); }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onOpenFull();
                                    }
                                }}
                            >
                                more
                            </span>
                        )}
                    </div>
                    <div className="adcard-media__footer">
                        <div className="adcard-media__footer-text">
                            <div className="adcard-media__headline">{headline || "—"}</div>
                            {description && <div className="adcard-media__desc">{description}</div>}
                            {href && mode === "full" && (
                                <a
                                    className="adcard-media__url"
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {prettyHost(href)}
                                </a>
                            )}
                        </div>
                        {cta && (href ? (
                            <a
                                className="adcard-media__cta adcard-media__cta--link"
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {cta}
                            </a>
                        ) : (
                            <span className="adcard-media__cta">{cta}</span>
                        ))}
                    </div>
                </>
            )}
        </>
    );
}
